import { useState, useRef } from 'react';
import { ChevronDown, LogOut } from 'lucide-react';
import { useAuthStore } from '@/features/auth/stores/auth-store';
import { useClickOutside } from '@/hooks';
import { OmniSearch } from './omni-search';

interface PageHeaderProps {
  title: string;
  badge?: string;
}

export function PageHeader({ title, badge = 'V3.4 PRO' }: PageHeaderProps) {
  const { user, logout } = useAuthStore();
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useClickOutside(menuRef, () => setIsUserMenuOpen(false));

  // Get user initials from first/last name, or fallback to email
  const userInitials = user?.firstName && user?.lastName
    ? `${user.firstName[0]}${user.lastName[0]}`.toUpperCase()
    : user?.firstName
    ? user.firstName.slice(0, 2).toUpperCase()
    : user?.email
    ? user.email.slice(0, 2).toUpperCase()
    : 'AD';

  // Profile dropdown name - use "First Last" or "First" or username
  const profileDisplayName = user?.firstName && user?.lastName
    ? `${user.firstName} ${user.lastName}`
    : user?.firstName
    ? user.firstName
    : user?.email?.split('@')[0] || 'User';

  return (
    <header className="bg-white border-b border-slate-200 px-6 py-4">
      <div className="flex items-center justify-between">
        {/* Title + Badge */}
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold text-slate-900">{title}</h1>
          {badge && (
            <span className="px-2 py-0.5 text-xs font-semibold text-slate-500 bg-slate-100 rounded uppercase tracking-wider">
              {badge}
            </span>
          )}
        </div>

        {/* Right side: Search + User */}
        <div className="flex items-center gap-4">
          {/* Omni-Search */}
          <OmniSearch />

          {/* User menu */}
          <div className="relative" ref={menuRef}>
            <button
              onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
              className="flex items-center gap-3 pl-3 pr-2 py-1.5 rounded-lg hover:bg-slate-50 transition-colors"
            >
              <div className="text-right hidden sm:block">
                <p className="text-sm font-medium text-slate-900">
                  {profileDisplayName}
                </p>
                <p className="text-xs text-slate-500">Tier 3 Enterprise</p>
              </div>
              <div className="h-9 w-9 rounded-full bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center text-sm font-bold text-white">
                {userInitials}
              </div>
              <ChevronDown
                size={16}
                className={`text-slate-400 transition-transform ${
                  isUserMenuOpen ? 'rotate-180' : ''
                }`}
              />
            </button>

            {/* Dropdown */}
            {isUserMenuOpen && (
              <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-lg border border-slate-200 py-1 animate-fade-in z-50">
                <div className="px-4 py-3 border-b border-slate-100">
                  <p className="text-sm font-medium text-slate-900 truncate">
                    {profileDisplayName}
                  </p>
                  <p className="text-xs text-slate-500 truncate">{user?.email || ''}</p>
                </div>
                <button
                  onClick={async () => {
                    setIsUserMenuOpen(false);
                    await logout();
                  }}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-rose-600 hover:bg-rose-50 transition-colors"
                >
                  <LogOut size={16} strokeWidth={1.5} />
                  Log Out
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}
